import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { purchaseApiRequests } from 'utils/api-requests/purchase';
import { userApiRequests } from 'utils/api-requests/user';
import { extraSmsToBuyOptions, extraTokenToBuyOptions } from 'utils/options';
import { convertQuantityForApi, getProductIdByType, initialState, POLLING_INTERVAL_MS, POLLING_TIMEOUT_MS } from 'utils/state-managment/order/productPurchase';
import { CartProductDetailDto, InvoiceDataDto, StatusDtoOrderStatusEnum } from 'utils/types/OrderModels';
import { ProductPurchaseStateType } from 'utils/types/ProductPurchase';
import { v4 } from 'uuid';

import { createPaymentLoadingContent } from 'components/common/Purchase/createPaymentLoadingContent';
import { hasErrors, InvoiceFormData, validateForm } from 'components/common/Purchase/ProductPurchaseForm';
import { PurchaseProductEnum } from 'components/common/Purchase/PurchaseStatusInfo';
import { activeNotification } from 'components/functions/activeNotification';

export interface ProductPurchaseContextType {
    purchaseState: ProductPurchaseStateType;
    handleInvoiceDataChange: (field: keyof InvoiceFormData, value: string) => void;
    handleQuantityChange: (productId: number, increase: boolean) => void;
    handleSubmitForm: (e: React.FormEvent) => void;
    handleCancel: () => void;
    clearState: () => void;
    getDisplayValue: (item: CartProductDetailDto) => number;
    canIncrease: (item: CartProductDetailDto) => boolean;
    canDecrease: (item: CartProductDetailDto) => boolean;
}

const values: ProductPurchaseContextType = {
    purchaseState: {} as ProductPurchaseStateType,
    handleInvoiceDataChange: () => {},
    handleQuantityChange: () => {},
    handleSubmitForm: () => {},
    handleCancel: () => {},
    clearState: () => {},
    getDisplayValue: () => 0,
    canIncrease: () => false,
    canDecrease: () => false
};

export const ProductPurchaseContext = createContext(values);

interface LocationState {
    quantity?: number;
}

export const ProductPurchaseProvider = ({ children }: { children: React.ReactNode }) => {
    const navigate = useNavigate();
    const { product } = useParams<{ product: string }>();
    const location = useLocation();
    const state = location.state as LocationState;

    const [purchaseState, setPurchaseState] = useState(initialState.productPurchaseInitialState());
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const getOptionsForCategory = useCallback((category: string) => {
        switch (category) {
        case 'SMS':
            return extraSmsToBuyOptions.map((opt) => parseInt(opt.value));
        case 'AI_TOKENS':
            return extraTokenToBuyOptions.map((opt) => parseInt(opt.value));
        default:
            return [];
        }
    }, []);

    const getDisplayValue = useCallback((item: CartProductDetailDto): number => {
        if (item.category === 'AI_TOKENS') {
            return item.quantity * 100000;
        } else if (item.category === 'SMS') {
            return item.quantity;
        }

        return item.quantity;
    }, []);

    const canIncrease = useCallback((item: CartProductDetailDto): boolean => {
        const currentDisplayValue = getDisplayValue(item);
        const availableOptions = getOptionsForCategory(item.category);
        const currentIndex = availableOptions.findIndex((option) => option === currentDisplayValue);

        return currentIndex >= 0 && currentIndex < availableOptions.length - 1;
    }, [getDisplayValue, getOptionsForCategory]);

    const canDecrease = useCallback((item: CartProductDetailDto): boolean => {
        const currentDisplayValue = getDisplayValue(item);
        const availableOptions = getOptionsForCategory(item.category);
        const currentIndex = availableOptions.findIndex((option) => option === currentDisplayValue);

        return currentIndex > 0;
    }, [getDisplayValue, getOptionsForCategory]);

    const initializeCart = useCallback(async () => {
        if (!product || !state?.quantity) {
            navigate('/my-settings');

            return;
        }

        setPurchaseState((prev) => ({ ...prev, isInitializing: true }));

        const productType = product === 'sms' ? PurchaseProductEnum.SMS : PurchaseProductEnum.TOKENS;

        await purchaseApiRequests.clearCart();

        const productId = getProductIdByType(productType);
        const apiQuantity = convertQuantityForApi(productType, state.quantity);
        const addedProduct = await purchaseApiRequests.addProductToCart(productId, apiQuantity);

        if (addedProduct) {
            const cartData = await purchaseApiRequests.getCartItems();

            setPurchaseState((prev) => ({
                ...prev,
                productType,
                initialQuantity: state.quantity!,
                cart: cartData,
                isInitializing: false
            }));
        } else {
            navigate('/my-settings');
        }
    }, [
        product,
        state?.quantity,
        navigate
    ]);

    const loadLastInvoiceData = useCallback(async () => {
        const lastInvoiceData = await purchaseApiRequests.getLastInvoiceData();

        if (lastInvoiceData) {
            // Odpowiedź może przyjść, gdy klient już edytuje formularz — uzupełniamy tylko puste pola, żeby nie nadpisać jego zmian
            setPurchaseState((prev) => ({
                ...prev,
                invoiceData: {
                    firstName: prev.invoiceData.firstName || lastInvoiceData.firstName || '',
                    lastName: prev.invoiceData.lastName || lastInvoiceData.lastName || '',
                    companyName: prev.invoiceData.companyName || lastInvoiceData.companyName || '',
                    taxId: prev.invoiceData.taxId || lastInvoiceData.taxId || '',
                    phoneNumber: prev.invoiceData.phoneNumber || lastInvoiceData.phoneNumber || '',
                    street: prev.invoiceData.street || lastInvoiceData.street || '',
                    city: prev.invoiceData.city || lastInvoiceData.city || '',
                    postalCode: prev.invoiceData.postalCode || lastInvoiceData.postalCode || ''
                }
            }));
        }
    }, []);

    const updateCartItem = useCallback(async (productId: number, quantity: number) => {
        setPurchaseState((prev) => ({ ...prev, isLoadingCart: true }));

        const result = await purchaseApiRequests.updateProductInCart(productId, quantity);

        if (result && purchaseState.cart) {
            const updatedItems = purchaseState.cart.items.map((item) => item.productId === productId
                ? { ...item, quantity, totalPrice: item.price * quantity }
                : item);
            const newTotalPrice = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);

            setPurchaseState((prev) => ({
                ...prev,
                cart: prev.cart
                    ? {
                        ...prev.cart,
                        items: updatedItems,
                        totalPrice: newTotalPrice
                    }
                    : null,
                isLoadingCart: false
            }));
        } else {
            const cartData = await purchaseApiRequests.getCartItems();
            setPurchaseState((prev) => ({
                ...prev,
                cart: cartData,
                isLoadingCart: false
            }));
        }
    }, [purchaseState.cart]);

    const clearCart = useCallback(async () => {
        setPurchaseState((prev) => ({ ...prev, isLoadingCart: true }));
        const success = await purchaseApiRequests.clearCart();

        if (success) {
            setPurchaseState((prev) => ({
                ...prev,
                cart: null,
                isLoadingCart: false
            }));
        } else {
            setPurchaseState((prev) => ({ ...prev, isLoadingCart: false }));
        }
    }, []);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }
        setPurchaseState((prev) => ({ ...prev, isPolling: false }));
    }, []);

    const handleSuccessfulPayment = useCallback(async () => {
        stopPolling();
        setPurchaseState((prev) => ({
            ...prev,
            processStep: 'Płatność zakończona pomyślnie!',
            status: StatusDtoOrderStatusEnum.COMPLETED,
            isLoading: false,
            showPurchaseStatus: true
        }));
    }, [stopPolling]);

    const handleFailedPayment = useCallback(() => {
        stopPolling();
        setPurchaseState((prev) => ({
            ...prev,
            status: StatusDtoOrderStatusEnum.FAILED,
            isLoading: false,
            isPolling: false,
            showPurchaseStatus: true
        }));
    }, [stopPolling]);

    const startPollingOrderStatus = useCallback((orderNumber: string) => {
        setPurchaseState((prev) => ({ ...prev, isPolling: true }));

        pollingIntervalRef.current = setInterval(async () => {
            const orderStatus = await purchaseApiRequests.getOrderStatus(orderNumber);

            if (orderStatus) {
                if (orderStatus.orderStatus === StatusDtoOrderStatusEnum.PAID ||
                    orderStatus.orderStatus === StatusDtoOrderStatusEnum.COMPLETED) {
                    await handleSuccessfulPayment();
                } else if (orderStatus.orderStatus === StatusDtoOrderStatusEnum.FAILED ||
                          orderStatus.orderStatus === StatusDtoOrderStatusEnum.CANCELLED ||
                          orderStatus.orderStatus === StatusDtoOrderStatusEnum.EXPIRED) {
                    handleFailedPayment();
                }

                setPurchaseState((prev) => ({
                    ...prev,
                    status: orderStatus.orderStatus
                }));
            }
        }, POLLING_INTERVAL_MS);

        pollingTimeoutRef.current = setTimeout(() => {
            stopPolling();
            activeNotification(
                'Timeout płatności',
                'Sprawdzanie statusu płatności zostało przerwane.',
                'warning'
            );
            setPurchaseState((prev) => ({
                ...prev,
                isLoading: false,
                processStep: 'Sprawdź status płatności w moich ustawieniach'
            }));
        }, POLLING_TIMEOUT_MS);
    }, [
        handleSuccessfulPayment,
        handleFailedPayment,
        stopPolling
    ]);

    useEffect(() => {
        initializeCart();
        loadLastInvoiceData();
    }, [initializeCart, loadLastInvoiceData]);

    const handleInvoiceDataChange = useCallback((field: keyof InvoiceFormData, value: string) => {
        setPurchaseState((prev) => ({
            ...prev,
            invoiceData: {
                ...prev.invoiceData,
                [field]: value
            },
            errors: {
                ...prev.errors,
                [field]: ''
            }
        }));
    }, []);

    const handleQuantityChange = useCallback(async (productId: number, increase: boolean) => {
        const item = purchaseState.cart?.items.find((i) => i.productId === productId);
        if (!item) return;

        const currentDisplayValue = getDisplayValue(item);
        const availableOptions = getOptionsForCategory(item.category);
        const currentIndex = availableOptions.findIndex((option) => option === currentDisplayValue);

        if (increase && currentIndex >= 0 && currentIndex < availableOptions.length - 1) {
            const nextDisplayValue = availableOptions[currentIndex + 1];
            const nextApiQuantity = item.category === 'AI_TOKENS'
                ? nextDisplayValue / 100000
                : nextDisplayValue;
            await updateCartItem(productId, nextApiQuantity);
        } else if (!increase && currentIndex > 0) {
            const prevDisplayValue = availableOptions[currentIndex - 1];
            const prevApiQuantity = item.category === 'AI_TOKENS'
                ? prevDisplayValue / 100000
                : prevDisplayValue;
            await updateCartItem(productId, prevApiQuantity);
        } else if (!increase && currentIndex === 0) {
            await clearCart();
        }
    }, [
        purchaseState.cart,
        getDisplayValue,
        getOptionsForCategory,
        updateCartItem,
        clearCart
    ]);

    const handleSubmitForm = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        setPurchaseState((prev) => ({ ...prev, triggerValidation: true }));

        const formErrors = validateForm(undefined, purchaseState.invoiceData, false);
        setPurchaseState((prev) => ({ ...prev, errors: formErrors }));

        if (hasErrors(formErrors)) {
            activeNotification('Formularz', 'Wypełnij poprawnie wszystkie wymagane pola', 'warning');

            return;
        }

        setPurchaseState((prev) => ({
            ...prev,
            isLoading: true,
            processStep: 'Tworzenie zamówienia...'
        }));

        const invoiceDataDto: InvoiceDataDto = {
            firstName: purchaseState.invoiceData.firstName,
            lastName: purchaseState.invoiceData.lastName,
            companyName: purchaseState.invoiceData.companyName,
            taxId: purchaseState.invoiceData.taxId,
            phoneNumber: purchaseState.invoiceData.phoneNumber,
            street: purchaseState.invoiceData.street,
            city: purchaseState.invoiceData.city,
            postalCode: purchaseState.invoiceData.postalCode
        };

        const createdOrder = await purchaseApiRequests.createOrder(invoiceDataDto);

        if (!createdOrder) {
            setPurchaseState((prev) => ({ ...prev, isLoading: false }));

            return;
        }

        setPurchaseState((prev) => ({
            ...prev,
            currentOrder: createdOrder,
            showPurchaseStatus: true,
            processStep: 'Przygotowywanie płatności...'
        }));

        const paymentWindow = window.open('about:blank', '_blank');

        if (paymentWindow) {
            createPaymentLoadingContent(paymentWindow);
        }

        const paymentUrl = await purchaseApiRequests.getPayTransactionOrder(createdOrder.orderNumer);

        if (!paymentUrl) {
            setPurchaseState((prev) => ({ ...prev, isLoading: false }));
            paymentWindow?.close();

            return;
        }

        if (paymentWindow && !paymentWindow.closed) {
            setTimeout(() => {
                paymentWindow.location.href = paymentUrl;
            }, 1500);
        } else {
            window.location.href = paymentUrl;

            return;
        }

        setPurchaseState((prev) => ({ ...prev, processStep: 'Oczekiwanie na płatność...' }));
        startPollingOrderStatus(createdOrder.orderNumer);
    }, [purchaseState.invoiceData, startPollingOrderStatus]);

    const handleCancel = useCallback(async () => {
        stopPolling();
        await purchaseApiRequests.clearCart();
        navigate('/my-settings');
    }, [navigate, stopPolling]);

    const clearState = useCallback(() => {
        setPurchaseState(initialState.productPurchaseInitialState());
    }, []);

    useEffect(() => {
        return () => {
            stopPolling();
            purchaseApiRequests.clearCart().catch((error) => {
                const errorId = v4();
                userApiRequests.logException(error, errorId);
            });
        };
    }, [stopPolling]);

    return (
        <ProductPurchaseContext.Provider
            value={{
                purchaseState,
                handleInvoiceDataChange,
                handleQuantityChange,
                handleSubmitForm,
                handleCancel,
                clearState,
                getDisplayValue,
                canIncrease,
                canDecrease
            }}
        >
            {children}
        </ProductPurchaseContext.Provider>
    );
};

